import { queryOptions } from '@tanstack/react-query';
import type { MenuCategory, MenuProduct } from '../model/schema';
import { menuApi, type MenuFilters, type MenuScope } from './menu-api';

/**
 * Query keys for the Menu catalog. The branch id is part of the key: switching
 * branches must not read another branch's menu from cache.
 */
export const menuKeys = {
  all: ['menu'] as const,
  /**
   * Every catalog entry, whatever its scope or filters — the invalidation
   * prefix. A write changes a product for all of them (the same product is
   * listed under «Все филиалы» and under each branch stocking it), so
   * mutations invalidate this rather than the on-screen entry alone.
   */
  catalogs: () => [...menuKeys.all, 'catalog'] as const,
  /**
   * The catalog under one scope and set of filters.
   *
   * The SCOPE is in the key because it selects a different route with
   * different prices — one branch's overlay vs the merchant-wide minimum (see
   * `menuApi.get`) — so the two answers must never be served for each other.
   * «Все филиалы» keys as `'all'` rather than `''`: a distinct entry from any
   * branch, and one that cannot collide with an unresolved scope.
   *
   * The filters are in the key too (normalized, so `undefined` and `''` are
   * one entry rather than two) because the request they produce is narrowed
   * server-side — keying without them would let a filtered answer be served as
   * if it were the whole menu.
   *
   * NOTE a write must invalidate EVERY scope, not just the one on screen: a
   * product edited under one branch also appears in «Все филиалы» and in any
   * other branch stocking it. `menuKeys.catalogs()` is the prefix for that;
   * this function is for reading one specific entry.
   */
  catalog: (restaurantId?: string, filters?: MenuFilters) =>
    [
      ...menuKeys.all,
      'catalog',
      restaurantId ?? 'all',
      // A blank/whitespace term is NOT a filter, and must collapse to the same
      // key as no term at all — otherwise clearing the box back to empty would
      // read a second, identical catalog under a different key.
      {
        search: normalizeSearch(filters?.search),
        status: filters?.status,
        // The sidebar's own filter is a THIRD axis of the same read (it
        // narrows the category list the request returns), so it belongs in the
        // key too — without it, picking a category status would be served the
        // previously-cached unfiltered taxonomy.
        categoryStatus: filters?.categoryStatus,
        showcase: filters?.showcase,
      },
    ] as const,
  /** Taxonomy-only read used by screens that do not render product cards. */
  categories: (
    restaurantId?: string,
    filters?: Pick<MenuFilters, 'categoryStatus' | 'showcase'>,
  ) =>
    [
      ...menuKeys.catalogs(),
      'categories',
      restaurantId ?? 'all',
      {
        categoryStatus: filters?.categoryStatus,
        showcase: filters?.showcase,
      },
    ] as const,
  /** One product, as read for the editor. The branch stays in the key so an edit under one branch never serves another branch's cached seed, even though the read itself is merchant-wide (no `restaurant_id` on the wire). */
  product: (restaurantId: string, id: MenuProduct['id']) =>
    [...menuKeys.all, 'product', restaurantId, String(id)] as const,
  /**
   * The merchant's whole catalog, for the branch editor's product picker. NOT
   * branch-scoped — see `menuApi.listMerchantProducts` — so the search term is
   * the only thing that varies it.
   */
  merchantProducts: (search: string) =>
    [...menuKeys.all, 'merchant-products', search] as const,
  /** One branch's stocked products, with that branch's own prices. */
  branchProducts: (restaurantId: string) =>
    [...menuKeys.all, 'branch-products', restaurantId] as const,
  /** Merchant-wide reusable modifier groups. */
  modifierGroups: () => [...menuKeys.all, 'modifier-groups'] as const,
  /** One category's translations. Merchant-wide, so no branch in the key. */
  category: (id: MenuCategory['id']) =>
    [...menuKeys.all, 'category', String(id)] as const,
};

/** A search term that actually narrows something, or `undefined`. */
function normalizeSearch(search: string | undefined): string | undefined {
  const trimmed = search?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

export const menuQueries = {
  catalog: (
    scope: MenuScope | undefined,
    filters?: MenuFilters,
    options?: { enabled?: boolean },
  ) =>
    queryOptions({
      queryKey: menuKeys.catalog(scope?.restaurantId, filters),
      queryFn: ({ signal }) => menuApi.get(scope, filters, { signal }),
      // No scope-based `enabled` gate. «Все филиалы» needs no branch at all,
      // and a branch scope only reaches here once one is resolved — the page
      // passes `undefined` until then, which reads the merchant-wide list
      // rather than sitting idle. The ONLY gate is the explicit one: showcase
      // (Витрина) mode reads each section from its own `*/published` endpoint,
      // so the page disables the sections that are not on screen and only the
      // active section's request fires.
      enabled: options?.enabled ?? true,
      // Typing in the search box walks through a key per keystroke. Keeping the
      // previous answer on screen means the grid narrows in place instead of
      // collapsing to a skeleton and back on every character.
      placeholderData: (previous) => previous,
    }),

  categories: (
    scope: MenuScope | undefined,
    filters?: Pick<MenuFilters, 'categoryStatus' | 'showcase'>,
    options?: { enabled?: boolean },
  ) =>
    queryOptions({
      queryKey: menuKeys.categories(scope?.restaurantId, filters),
      queryFn: ({ signal }) => menuApi.listCategories(scope, filters, { signal }),
      enabled: options?.enabled ?? true,
      placeholderData: (previous) => previous,
    }),

  /**
   * One product's authoritative record, for seeding the edit drawer. Kept
   * separate from the catalog query because it is fetched on demand (only when
   * the drawer opens on an existing product) and answers with fields the list
   * payload doesn't carry.
   *
   * `staleTime: 0` so re-opening the drawer re-reads rather than replaying a
   * stale record over a form the merchant is about to submit.
   */
  product: (scope: MenuScope, id: MenuProduct['id'] | undefined) =>
    queryOptions({
      queryKey: menuKeys.product(scope.restaurantId, id ?? ''),
      // `enabled` guards the undefined case, so the id is always present here.
      queryFn: () => menuApi.getProduct(scope, id ?? ''),
      enabled: scope.restaurantId !== '' && id !== undefined,
      staleTime: 0,
    }),

  /**
   * The merchant's whole catalog, for the branch editor's product picker.
   *
   * `search` is passed to the API rather than filtered in memory: the catalog
   * is unbounded (the design's own footer counts «из 100»), so narrowing it
   * client-side would only ever narrow the page that happened to load.
   */
  merchantProducts: (search: string) =>
    queryOptions({
      queryKey: menuKeys.merchantProducts(search),
      queryFn: ({ signal }) =>
        menuApi.listMerchantProducts(search ? { name: search } : undefined, { signal }),
    }),

  /**
   * One branch's stocked products — what seeds step 2 of the branch editor on
   * an EDIT. Idle until a branch id is known, which a create only has after
   * step 1 saves.
   *
   * `staleTime: 0` so reopening the editor re-reads: the rows are the basis for
   * the diff step 2 submits, and a stale set would attach or detach the wrong
   * products.
   */
  branchProducts: (restaurantId: string | undefined) =>
    queryOptions({
      queryKey: menuKeys.branchProducts(restaurantId ?? ''),
      queryFn: ({ signal }) => menuApi.listBranchProducts(restaurantId ?? '', { signal }),
      enabled: restaurantId !== undefined && restaurantId !== '',
      staleTime: 0,
    }),

  /** Reusable modifier groups that can be attached to the product by id. */
  modifierGroups: () =>
    queryOptions({
      queryKey: menuKeys.modifierGroups(),
      queryFn: ({ signal }) => menuApi.listModifierGroups({ signal }),
    }),

  /**
   * One category's translations, for seeding the rename dialog. Not
   * branch-scoped — categories are merchant-wide (see `menuApi.get`).
   */
  category: (id: MenuCategory['id'] | undefined) =>
    queryOptions({
      queryKey: menuKeys.category(id ?? ''),
      queryFn: () => menuApi.getCategory(id ?? ''),
      enabled: id !== undefined,
      staleTime: 0,
    }),
};
